import React from 'react';
import {
  DatarouterError,
  emailUtils,
  LoadingStates,
  RentalManagerUserManagementCompanyWebApiHandler_addSingleUmcListingPermission,
} from '@zg-rentals/rental-manager-env';
import {
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Input,
  Label,
  Modal,
  Spacer,
  TextButton,
} from '@zillow/constellation-10';

interface AddListingPermissionModalProps {
  onClose: (refresh: boolean) => void;
  listingAlias: string;
  shouldOpen?: boolean;
}

export const AddListingPermissionModal = ({
  onClose,
  listingAlias,
  shouldOpen = false,
}: AddListingPermissionModalProps) => {
  const [email, setEmail] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [loadingState, setLoadingState] = React.useState(LoadingStates.INITIAL);
  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setErrorMessage('');
    setLoadingState(LoadingStates.INITIAL);
    onClose(false);
  };

  const validEmail = emailUtils.isValid(email);

  const renderBody = () => (
    <Spacer css={{ padding: 'md' }}>
      <FormField>
        <Label>Email</Label>
        <Input value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} />
        {errorMessage && (
          <FormHelp error css={{ marginBlockStart: 'xs' }}>
            {errorMessage}
          </FormHelp>
        )}
      </FormField>
    </Spacer>
  );

  const handleSubmitPermission = () => {
    if (!validEmail) {
      setErrorMessage('Invalid email');
    }
    setLoadingState(LoadingStates.LOADING);
    setErrorMessage('');
    RentalManagerUserManagementCompanyWebApiHandler_addSingleUmcListingPermission({ listingAlias, email })
      .then(() => {
        setLoadingState(LoadingStates.SUCCESS);
        setErrorMessage('');
        onClose(true);
        return;
      })
      .catch((error) => {
        setLoadingState(LoadingStates.ERROR);
        setErrorMessage(DatarouterError.getDisplayMessage(error));
      });
  };

  React.useEffect(() => {
    if (shouldOpen) setOpen(true);
  }, [shouldOpen]);

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) handleClose();
      }}
      size="sm"
      header={<Heading level={6}>Add a new user to manage your listing</Heading>}
      body={renderBody()}
      footer={
        <ButtonGroup aria-label="Modal actions">
          <TextButton onClick={handleClose}>Cancel</TextButton>
          <Button onClick={handleSubmitPermission} disabled={!email} loading={loadingState === LoadingStates.LOADING}>
            Confirm
          </Button>
        </ButtonGroup>
      }
    />
  );
};
